import { useEffect, useState } from 'react';
import { getDepartments, createDepartment, updateDepartment, deleteDepartment, getUsers, setDepartmentHead, setDepartmentDeputies } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Building2, Plus, Pencil, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const Departments = () => {
  const { isSuperAdmin } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    info_email: '',
    notification_emails: '',
    is_active: true,
    allow_department_email_edit: false
  });
  const [headUserId, setHeadUserId] = useState('');
  const [deputyUserIds, setDeputyUserIds] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [deptsData, usersData] = await Promise.all([getDepartments(), getUsers()]);
      setDepartments(deptsData);
      setUsers(usersData);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      info_email: '',
      notification_emails: '',
      is_active: true,
      allow_department_email_edit: false
    });
    setHeadUserId('');
    setDeputyUserIds([]);
    setEditingDept(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (deputyUserIds.length > 2) {
      toast.error('Maximal 2 Stellvertreter erlaubt');
      return;
    }
    
    if (headUserId && deputyUserIds.includes(headUserId)) {
      toast.error('Abteilungsleiter darf nicht gleichzeitig Stellvertreter sein');
      return;
    }
    
    try {
      const payload = {
        ...formData,
        notification_emails: formData.notification_emails ? formData.notification_emails.split(',').map(e => e.trim()).filter(e => e) : []
      };
      
      let dept;
      if (editingDept) {
        dept = await updateDepartment(editingDept.id, payload);
      } else {
        dept = await createDepartment(payload);
      }
      
      // Set head and deputies
      if (headUserId && headUserId !== '__none__') {
        await setDepartmentHead(dept.id, headUserId);
      }
      
      if (deputyUserIds.length > 0) {
        await setDepartmentDeputies(dept.id, deputyUserIds);
      }
      
      toast.success(editingDept ? 'Abteilung aktualisiert' : 'Abteilung erstellt');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleEdit = (dept) => {
    setEditingDept(dept);
    setFormData({
      name: dept.name,
      description: dept.description || '',
      info_email: dept.info_email || '',
      notification_emails: (dept.notification_emails || []).join(', '),
      is_active: dept.is_active,
      allow_department_email_edit: dept.allow_department_email_edit || false
    });
    setHeadUserId(dept.head_user_id || '__none__');
    setDeputyUserIds(dept.deputy_user_ids || []);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Abteilung wirklich löschen?')) return;
    try {
      await deleteDepartment(id);
      toast.success('Abteilung gelöscht');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const toggleDeputy = (userId) => {
    if (deputyUserIds.includes(userId)) {
      setDeputyUserIds(deputyUserIds.filter(id => id !== userId));
    } else {
      if (deputyUserIds.length >= 2) {
        toast.error('Maximal 2 Stellvertreter erlaubt');
        return;
      }
      setDeputyUserIds([...deputyUserIds, userId]);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Abteilungen</h1>
          <p className="text-gray-500 mt-1">Verwalten Sie alle Abteilungen</p>
        </div>
        {isSuperAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Neue Abteilung
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingDept ? 'Abteilung bearbeiten' : 'Neue Abteilung'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Name *</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                  </div>
                  <div>
                    <Label>Info E-Mail</Label>
                    <Input type="email" value={formData.info_email} onChange={(e) => setFormData({ ...formData, info_email: e.target.value })} placeholder="info@abteilung.de" />
                  </div>
                </div>
                <div>
                  <Label>Beschreibung</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
                </div>
                <div>
                  <Label>Benachrichtigungs-E-Mails (kommagetrennt)</Label>
                  <Textarea value={formData.notification_emails} onChange={(e) => setFormData({ ...formData, notification_emails: e.target.value })} placeholder="admin1@example.com, admin2@example.com" rows={2} />
                </div>
                <div>
                  <Label>Abteilungsleiter</Label>
                  <Select value={headUserId} onValueChange={setHeadUserId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Abteilungsleiter wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Kein Leiter</SelectItem>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.full_name || user.username} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Stellvertreter (max. 2)</Label>
                  <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                    {users.map((user) => (
                      <div key={user.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={deputyUserIds.includes(user.id)}
                          onChange={() => toggleDeputy(user.id)}
                          disabled={headUserId === user.id}
                          className="rounded"
                        />
                        <span className="text-sm">{user.full_name || user.username}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{deputyUserIds.length}/2 Stellvertreter ausgewählt</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.allow_department_email_edit}
                    onChange={(e) => setFormData({ ...formData, allow_department_email_edit: e.target.checked })}
                    className="rounded"
                  />
                  <Label>Abteilungsleiter darf E-Mails bearbeiten</Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded"
                  />
                  <Label>Aktiv</Label>
                </div>
                <div className="flex gap-2 justify-end pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
                  <Button type="submit">Speichern</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {departments.map((dept) => (
          <Card key={dept.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Building2 className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{dept.name}</CardTitle>
                    {!dept.is_active && <span className="text-xs text-red-500">Inaktiv</span>}
                  </div>
                </div>
                {isSuperAdmin() && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(dept)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(dept.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-gray-600">{dept.description || 'Keine Beschreibung'}</p>
              {dept.info_email && (
                <div className="text-sm">
                  <span className="font-medium">Info:</span> {dept.info_email}
                </div>
              )}
              {dept.head_user_name && (
                <div className="text-sm flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  <span className="font-medium">Leiter:</span> {dept.head_user_name}
                </div>
              )}
              {dept.deputy_user_names && dept.deputy_user_names.length > 0 && (
                <div className="text-sm">
                  <span className="font-medium">Stellvertreter:</span> {dept.deputy_user_names.join(', ')}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Departments;